import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, ChevronDown, ChevronUp } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  needsConsentBanner,
  saveConsent,
  applyConsent,
} from "@/lib/consent";

const HIDDEN_PATHS = ["/admin", "/login"];
const EVENT_ROUTE_PREFIX = "/e/";

export function shouldHideCookieBanner(pathname: string): boolean {
  return HIDDEN_PATHS.some((p) => pathname.startsWith(p))
    || pathname.includes("/admin")
    || pathname.startsWith(EVENT_ROUTE_PREFIX);
}

function Toggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        enabled ? "bg-primary" : "bg-muted"
      }`}
    >
      <span
        className="pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow-sm transition-transform"
        style={{ transform: enabled ? "translateX(20px)" : "translateX(2px)" }}
      />
    </button>
  );
}

export function CookieBanner() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const isHr = !i18n.resolvedLanguage?.startsWith("en");

  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    setVisible(needsConsentBanner());
  }, []);

  const isHiddenPath = shouldHideCookieBanner(location.pathname);

  if (!visible || isHiddenPath) return null;

  const accept = (all: boolean) => {
    const prefs = saveConsent(all || analytics, all || marketing);
    applyConsent(prefs);
    setVisible(false);
  };

  const saveManaged = () => {
    const prefs = saveConsent(analytics, marketing);
    applyConsent(prefs);
    setVisible(false);
  };

  const text = {
    title: isHr ? "Kolačići i privatnost" : "Cookies & privacy",
    body: isHr
      ? "Koristimo kolačiće za analitiku i oglašavanje kako bismo bolje razumjeli naše posjetitelje i poboljšali uslugu."
      : "We use cookies for analytics and advertising to understand our visitors and improve our service.",
    manage: isHr ? "Upravljaj postavkama" : "Manage preferences",
    reject: isHr ? "Odbij sve" : "Reject all",
    accept: isHr ? "Prihvati sve" : "Accept all",
    save: isHr ? "Spremi postavke" : "Save preferences",
    necessary: isHr ? "Nužni (uvijek aktivni)" : "Necessary (always on)",
    necessaryDesc: isHr
      ? "Tehnička pohrana potrebna za funkcioniranje usluge."
      : "Technical storage required for the service to work.",
    analyticsLabel: isHr ? "Analitika" : "Analytics",
    analyticsDesc: isHr
      ? "Google Analytics 4 — pomažu nam razumjeti kako koristite stranicu."
      : "Google Analytics 4 — helps us understand how you use the site.",
    marketingLabel: isHr ? "Oglašavanje" : "Marketing",
    marketingDesc: isHr
      ? "Meta Pixel, TikTok Pixel, Google Ads — za prikazivanje relevantnih oglasa."
      : "Meta Pixel, TikTok Pixel, Google Ads — for showing relevant ads.",
    privacyLink: isHr ? "Politika privatnosti" : "Privacy policy",
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-banner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-[380px]"
          role="dialog"
          aria-label={text.title}
          aria-modal="false"
        >
          <div className="overflow-hidden rounded-2xl border border-border bg-background shadow-2xl shadow-black/10 dark:bg-background dark:shadow-black/30">
            {/* Header */}
            <div className="flex items-start gap-3 p-4 pb-3">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Cookie className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-display font-semibold text-foreground leading-snug">{text.title}</p>
                <p className="mt-1 text-xs font-body text-muted-foreground leading-relaxed">{text.body}</p>
                <Link
                  to="/privatnost"
                  className="mt-1 inline-block text-[11px] font-body text-primary hover:underline underline-offset-2"
                >
                  {text.privacyLink}
                </Link>
              </div>
            </div>

            {/* Expanded preferences */}
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-border/60 mx-4" />
                  <div className="px-4 py-3 space-y-3">
                    {/* Necessary — always on */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-body font-semibold text-foreground">{text.necessary}</p>
                        <p className="text-[11px] font-body text-muted-foreground leading-snug mt-0.5">{text.necessaryDesc}</p>
                      </div>
                      <div className="shrink-0 mt-0.5">
                        <Toggle enabled={true} onChange={() => {}} />
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-body font-semibold text-foreground">{text.analyticsLabel}</p>
                        <p className="text-[11px] font-body text-muted-foreground leading-snug mt-0.5">{text.analyticsDesc}</p>
                      </div>
                      <div className="shrink-0 mt-0.5">
                        <Toggle enabled={analytics} onChange={setAnalytics} />
                      </div>
                    </div>

                    {/* Marketing */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-body font-semibold text-foreground">{text.marketingLabel}</p>
                        <p className="text-[11px] font-body text-muted-foreground leading-snug mt-0.5">{text.marketingDesc}</p>
                      </div>
                      <div className="shrink-0 mt-0.5">
                        <Toggle enabled={marketing} onChange={setMarketing} />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="px-4 pb-4 pt-2 space-y-2">
              {expanded ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    className="inline-flex items-center gap-1 text-xs font-body text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                    {isHr ? "Natrag" : "Back"}
                  </button>
                  <button
                    type="button"
                    onClick={saveManaged}
                    className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-xs font-body font-semibold text-foreground hover:bg-muted transition-colors"
                  >
                    {text.save}
                  </button>
                </div>
              ) : (
                <>
                  {/* Equal-weight primary choices — same size, same style */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => accept(false)}
                      className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-xs font-body font-semibold text-foreground hover:bg-muted transition-colors"
                    >
                      {text.reject}
                    </button>
                    <button
                      type="button"
                      onClick={() => accept(true)}
                      className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-xs font-body font-semibold text-foreground hover:bg-muted transition-colors"
                    >
                      {text.accept}
                    </button>
                  </div>
                  {/* Manage — secondary navigation, not a consent choice */}
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-body text-muted-foreground hover:text-foreground transition-colors py-0.5"
                  >
                    {text.manage}
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
